import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../../../app/store';
import { OUT_OF_SESSION } from '../../../constants';
import { EventTypes } from '../../../constants/event';
import {
  IMessageReceived,
  messagingActions,
} from '../../../reducers/messagingSlice';
import {
  selectRestaurant,
  selectStage,
} from '../../../reducers/restaurantSlice';
import logger from '../../../utils/logger';
import { resetSessionBoundaryData } from '../sessionBoundary/sessionBoundary.slice';
import { MAX_TRACKED_SESSIONS, SLICE_NAME } from './experiments.constants';
import {
  IExperimentAssignment,
  IExperimentsState,
  IPromptSlot,
} from './experiments.props';
import { selectNextPromptAskCount } from './experiments.selector';
import {
  currentCarSessionId,
  extractExperimentAssignment,
  toPromptSlotKey,
} from './experiments.utils';

const initialState: IExperimentsState = {
  bySession: {},
  sessionOrder: [],
};

const describeGroups = (assignment: IExperimentAssignment) =>
  Object.entries(assignment.experiments ?? {})
    .map(([experiment, { group }]) => `${experiment}=${group}`)
    .join(', ') || 'none';

// Starts tracking a car, dropping the oldest once more than MAX_TRACKED_SESSIONS are held.
const trackSession = (state: IExperimentsState, sessionId: string) => {
  if (state.bySession[sessionId]) return state.bySession[sessionId];
  state.bySession[sessionId] = { assignment: null, promptAskCounts: {} };
  state.sessionOrder.push(sessionId);
  while (state.sessionOrder.length > MAX_TRACKED_SESSIONS) {
    delete state.bySession[state.sessionOrder.shift() as string];
  }
  return state.bySession[sessionId];
};

// Keeps only the given cars.
const keepSessions = (state: IExperimentsState, keep: string[]) => {
  state.sessionOrder = state.sessionOrder.filter((sessionId) => {
    if (keep.includes(sessionId)) return true;
    delete state.bySession[sessionId];
    return false;
  });
};

export const experimentsSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetExperiments: () => {
      return initialState;
    },
    promptAskedForSession: (
      state,
      {
        payload: { sessionId, slotKey, nextAskCount },
      }: PayloadAction<{
        sessionId: string;
        slotKey: string;
        nextAskCount: number;
      }>
    ) => {
      if (!sessionId || sessionId === OUT_OF_SESSION) return;
      trackSession(state, sessionId).promptAskCounts[slotKey] = nextAskCount;
    },
    // A filled slot's next ask starts the prompt over.
    promptSlotsFilled: (
      state,
      {
        payload: { sessionId, slotKeys },
      }: PayloadAction<{ sessionId: string; slotKeys: string[] }>
    ) => {
      const counts = state.bySession[sessionId]?.promptAskCounts;
      if (!counts) return;
      slotKeys.forEach((slotKey) => delete counts[slotKey]);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      messagingActions.messageReceived,
      (state, action: PayloadAction<IMessageReceived>) => {
        const { taskRouter, sessionBoundarySessionId } = action.payload;
        const consoleSessionId = currentCarSessionId({
          currentTaskId: taskRouter?.currentTaskId,
          taskAssignedSessionId: taskRouter?.taskAssignedSessionId,
          sessionBoundarySessionId,
        });
        // Like aiSuggestions: only the task router can say a frame is another car's.
        // sessionBoundary still holds the previous car at a back-to-back car_enter.
        const taskRouterSessionId = taskRouter?.currentTaskId
          ? taskRouter.taskAssignedSessionId
          : '';
        const isRealSession = (sessionId: string) =>
          !!sessionId && sessionId !== OUT_OF_SESSION;

        action.payload.messages.forEach((message) => {
          const { session_id: sessionId, event } = message;
          if (!isRealSession(sessionId)) return;
          const isOtherTaskCar =
            !!taskRouterSessionId && sessionId !== taskRouterSessionId;
          if (event === EventTypes.carEnter) {
            // A new car keeps only itself and the car still on the console.
            if (isOtherTaskCar || state.bySession[sessionId]) return;
            keepSessions(state, [sessionId, consoleSessionId]);
            trackSession(state, sessionId);
            return;
          }
          const assignment = extractExperimentAssignment(message);
          if (!assignment) return;
          if (isOtherTaskCar) {
            logger.info(
              `Experiment assignment for session ${sessionId} dropped: the current car is ${taskRouterSessionId}`
            );
            return;
          }
          const tracked = trackSession(state, sessionId);
          if (
            JSON.stringify(tracked.assignment) !== JSON.stringify(assignment)
          ) {
            logger.info(
              `Experiment assignment accepted for session ${sessionId}: ${describeGroups(
                assignment
              )}`
            );
          }
          tracked.assignment = assignment;
        });
      }
    );
    builder.addMatcher(
      isAnyOf(
        resetSessionBoundaryData,
        selectRestaurant.fulfilled,
        selectStage.fulfilled
      ),
      () => {
        return initialState;
      }
    );
  },
});

const { resetExperiments, promptAskedForSession, promptSlotsFilled } =
  experimentsSlice.actions;

// A spoken ask of this slot's prompt for the current car, moving its cursor past the phrase spoken.
const promptAsked =
  (slot: IPromptSlot, menuPrompt: string | string[] | undefined): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const { taskRouter, sessionBoundary } = state;
    dispatch(
      promptAskedForSession({
        nextAskCount: selectNextPromptAskCount(state, slot, menuPrompt),
        sessionId: currentCarSessionId({
          currentTaskId: taskRouter?.currentTaskId,
          taskAssignedSessionId: taskRouter?.taskAssignedSessionId,
          sessionBoundarySessionId: sessionBoundary?.sessionId,
        }),
        slotKey: toPromptSlotKey(slot),
      })
    );
  };

export { initialState, resetExperiments, promptAsked, promptSlotsFilled };
export default experimentsSlice.reducer;
